import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { transformToCartProducts, transformFromCartProducts, Products } from '@/hooks/useCart'
import { toISO } from '@/utils/formatDate'
import { buildRecurrentDateList } from '@/components/pages/pet-sitter/reservations/NewRequest/requestConfig'

export type ServiceSlug = 'promenade' | 'garde-chez-vous' | 'garde-chez-eux' | 'visite'

interface DateTimeInstance {
  date?: string
  dateStart?: string
  dateEnd?: string
  frequency?: 'once' | 'daily' | 'every_2_days' | 'every_3_days' | 'custom'
  customDays?: {
    scheduleEveryMonday: boolean
    scheduleEveryTuesday: boolean
    scheduleEveryWednesday: boolean
    scheduleEveryThursday: boolean
    scheduleEveryFriday: boolean
    scheduleEverySaturday: boolean
    scheduleEverySunday: boolean
  }
  timeStart?: string
  timeEnd?: string
  serviceDayCount?: number
  serviceDuration?: string
  timePeriods?: Array<{ timeStart?: string; timeEnd?: string; serviceDuration?: string }>
}

const STORE_NAME = 'new-request-cart-store'

export type FormSlot = { id: string; type: ServiceSlug; data: DateTimeInstance | null }

export type AdditionalInformations = {
  walkBehaviors: string[]
  indoorCleanliness?: 'never' | 'rare' | 'often'
  indoorBehaviors: string[]
  timeAlone?: string
  housing?: string
  summary?: string
}

export type NewRequestDraft = {
  address?: any
  distance?: { min: number; max: number }
  icon?: string
  message?: string
  additionalInformations?: AdditionalInformations
  animals?: any[]
  petsittersIds?: string[]
  service?: {
    selectedService?: string
    formSlots?: Record<ServiceSlug, FormSlot[]>
  }
}

export type SlotsByService = Record<ServiceSlug, FormSlot[]>

export type PetToComplete = { slug: 'dog' | 'cat' | 'nac'; number: number }

const MAX_PETSITTERS = 10

type NewRequestCartState = {
  lastUpdate: number
  setLastUpdate: (timestamp: number) => void

  hasHydrated: boolean
  setHasHydrated: (v: boolean) => void

  initialized: boolean
  setInitialized: (v: boolean) => void

  hasCompleteSearch: boolean
  setHasCompleteSearch: (hasCompleteSearch: boolean) => void

  address?: any
  distance: { min: number; max: number }
  icon?: string
  message: string
  additionalInformations: AdditionalInformations
  animals: any[]

  selectedService: string | null
  formSlotsByService: SlotsByService

  petsittersIds: string[]
  petsToComplete: PetToComplete[]

  hydrateFromPersistedState: (state: Partial<NewRequestCartState>) => void

  setPetsittersIds: (ids: string[]) => void
  addPetsitter: (id: string) => void
  removePetsitter: (id: string) => void
  clearPetsitters: () => void

  setPetsToComplete: (pets: PetToComplete[]) => void
  updatePetToComplete: (slug: 'dog' | 'cat' | 'nac', number: number) => void

  initFromSignUpDraft: (draft: NewRequestDraft) => void
  reset: () => void

  setAddress: (address?: any) => void
  setDistance: (distance: { min: number; max: number }) => void
  setIcon: (icon?: string) => void
  setMessage: (message: string) => void
  setAdditionalInformations: (info: AdditionalInformations) => void
  patchAdditionalInformations: (patch: Partial<AdditionalInformations>) => void
  setAnimals: (animals: any[]) => void

  setSelectedService: (service: string | null) => void

  setFormSlotsByService: (next: SlotsByService) => void
  updateFormSlotsByService: (updater: (prev: SlotsByService) => SlotsByService) => void

  addSlot: (slug: ServiceSlug, slot: FormSlot) => void
  saveSlot: (slug: ServiceSlug, slotId: string, instance: DateTimeInstance) => void
  removeSlot: (slug: ServiceSlug, slotId: string, makeBlankSlot: (slug: ServiceSlug) => FormSlot) => void

  getCartPayload: () => {
    products: Products[]
    petsitters: string[]
    animals: any[]
    message: string
    allowAutoRebound: boolean
    allowTenders: boolean
  } | null

  getServiceValues: () => ReturnType<typeof transformFromCartProducts> | null
}

const defaultAdditionalInformations: AdditionalInformations = {
  walkBehaviors: [],
  indoorBehaviors: [],
  indoorCleanliness: undefined,
  timeAlone: undefined,
  housing: undefined,
  summary: '',
}

export const defaultSlots = (): SlotsByService => ({
  promenade: [
    {
      id: 'default-promenade',
      type: 'promenade',
      data: {
        timePeriods: [{ timeStart: '', serviceDuration: '' }],
        date: '',
        frequency: 'once',
        serviceDayCount: 1,
      },
    },
  ],
  'garde-chez-vous': [],
  'garde-chez-eux': [],
  visite: [
    {
      id: 'default-visite',
      type: 'visite',
      data: {
        timePeriods: [{ timeStart: '', serviceDuration: '' }],
        date: '',
        frequency: 'once',
        serviceDayCount: 1,
      },
    },
  ],
})

const defaultState = (): Omit<
  NewRequestCartState,
  | 'setHasHydrated'
  | 'setInitialized'
  | 'initFromSignUpDraft'
  | 'setHasCompleteSearch'
  | 'reset'
  | 'setAddress'
  | 'setDistance'
  | 'setIcon'
  | 'setMessage'
  | 'setAdditionalInformations'
  | 'patchAdditionalInformations'
  | 'setAnimals'
  | 'setSelectedService'
  | 'setFormSlotsByService'
  | 'updateFormSlotsByService'
  | 'hydrateFromPersistedState'
  | 'setPetsToComplete'
  | 'updatePetToComplete'
  | 'setPetsittersIds'
  | 'addPetsitter'
  | 'removePetsitter'
  | 'clearPetsitters'
  | 'addSlot'
  | 'saveSlot'
  | 'removeSlot'
  | 'setLastUpdate'
  | 'getCartPayload'
  | 'getServiceValues'
> => ({
  lastUpdate: Date.now(),
  hasHydrated: false,
  initialized: false,
  hasCompleteSearch: false,
  address: undefined,
  distance: { min: 0, max: 20 },
  icon: undefined,
  message: '',
  additionalInformations: defaultAdditionalInformations,
  animals: [],
  selectedService: null,
  formSlotsByService: defaultSlots(),
  petsittersIds: [],
  petsToComplete: [
    { slug: 'dog', number: 0 },
    { slug: 'cat', number: 0 },
    { slug: 'nac', number: 0 },
  ],
})

export const useNewRequestCartStore = create<NewRequestCartState>()(
  persist(
    (set, get) => ({
      ...defaultState(),

      setHasHydrated: (v) => set({ hasHydrated: v }),
      setInitialized: (v) => set({ initialized: v }),

      initFromSignUpDraft: (draft) => {
        if (get().initialized) return

        set({
          initialized: true,
          address: draft.address,
          distance: draft.distance ?? { min: 0, max: 20 },
          icon: draft.icon,
          message: draft.message ?? '',
          additionalInformations: draft.additionalInformations ?? defaultAdditionalInformations,
          animals: draft.animals ?? [],
          petsittersIds: draft.petsittersIds ?? [],
          selectedService: draft.service?.selectedService ?? null,
          formSlotsByService: draft.service?.formSlots ?? defaultSlots(),
        })
      },

      reset: () => set({ ...defaultState(), hasHydrated: true } as Partial<NewRequestCartState>),

      setHasCompleteSearch: (hasCompleteSearch) => set({ hasCompleteSearch }),

      setAddress: (address) => set({ address }),
      setDistance: (distance) => set({ distance }),
      setIcon: (icon) => set({ icon }),
      setMessage: (message) => set({ message }),

      setAdditionalInformations: (info) => set({ additionalInformations: info }),
      patchAdditionalInformations: (patch) =>
        set((state) => ({
          additionalInformations: { ...state.additionalInformations, ...patch },
        })),

      setAnimals: (animals) => set({ animals }),

      setSelectedService: (service) => set({ selectedService: service }),

      setPetsittersIds: (ids) =>
        set({
          petsittersIds: ids.slice(0, MAX_PETSITTERS),
          lastUpdate: Date.now(),
        }),

      addPetsitter: (id) =>
        set((state) => {
          if (state.petsittersIds.includes(id)) return state
          if (state.petsittersIds.length >= MAX_PETSITTERS) return state

          return {
            petsittersIds: [...state.petsittersIds, id],
            lastUpdate: Date.now(),
          }
        }),

      removePetsitter: (id) =>
        set((state) => ({
          petsittersIds: state.petsittersIds.filter((petsitterId) => petsitterId !== id),
          lastUpdate: Date.now(),
        })),

      clearPetsitters: () => set({ petsittersIds: [] }),

      setFormSlotsByService: (next) => set({ formSlotsByService: next }),
      updateFormSlotsByService: (updater) =>
        set((state) => ({ formSlotsByService: updater(state.formSlotsByService) })),

      setPetsToComplete: (pets) => set({ petsToComplete: pets }),
      updatePetToComplete: (slug, number) =>
        set((state) => ({
          petsToComplete: state.petsToComplete.map((pet) => (pet.slug === slug ? { ...pet, number } : pet)),
        })),

      addSlot: (slug, slot) =>
        set((state) => ({
          formSlotsByService: {
            ...state.formSlotsByService,
            [slug]: [...(state.formSlotsByService[slug] ?? []), slot],
          },
        })),

      saveSlot: (slug, slotId, instance) =>
        set((state) => ({
          formSlotsByService: {
            ...state.formSlotsByService,
            [slug]: (state.formSlotsByService[slug] ?? []).map((s) => (s.id === slotId ? { ...s, data: instance } : s)),
          },
        })),

      removeSlot: (slug, slotId, makeBlankSlot) =>
        set((state) => {
          const remaining = (state.formSlotsByService[slug] ?? []).filter((s) => s.id !== slotId)
          return {
            formSlotsByService: {
              ...state.formSlotsByService,
              [slug]: remaining.length ? remaining : [makeBlankSlot(slug)],
            },
          }
        }),

      hydrateFromPersistedState: (persisted) =>
        set((state) => {
          const incomingLastUpdate = persisted.lastUpdate ?? 0
          const currentLastUpdate = state.lastUpdate ?? 0

          if (incomingLastUpdate <= currentLastUpdate) {
            return state
          }

          return {
            ...state,
            ...persisted,
            hasHydrated: true,
          }
        }),

      setLastUpdate: (timestamp) => set({ lastUpdate: timestamp }),
      getCartPayload: () => {
        const state = get()

        if (!state.selectedService) return null

        const slug = state.selectedService as ServiceSlug

        const rawInstances = state.formSlotsByService[slug]?.map((slot) => slot.data).filter(Boolean) ?? []

        // ✅ only expand for garde services
        const shouldExpand = slug === 'promenade' || slug === 'visite'

        const instances = shouldExpand
          ? rawInstances.flatMap((instance) =>
              expandDateTimeInstance(instance!, slug)
                .filter((i) => i != null)
                .map((i) => ({
                  ...i,
                  start: i ? toISO(i.date!, i.timePeriods?.[0]?.timeStart) : undefined,
                })),
            )
          : rawInstances.map((i) =>
              i
                ? {
                    ...i,
                    start: toISO(i.date!, i.timePeriods?.[0]?.timeStart),
                  }
                : i,
            )

        const products = transformToCartProducts(slug, instances)

        return {
          products,
          petsitters: state.petsittersIds,
          animals: state.animals,
          message: state.message,
          allowAutoRebound: false,
          allowTenders: true,
        }
      },
      getServiceValues: () => {
        const state = get()

        if (!state.selectedService) return null

        const instances =
          state.formSlotsByService[state.selectedService as ServiceSlug]?.map((slot) => slot.data).filter(Boolean) ?? []

        const products = transformToCartProducts(state.selectedService, instances)

        return transformFromCartProducts(products)
      },
    }),
    {
      name: STORE_NAME,
      version: 1,
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
      partialize: (state) => ({
        lastUpdate: state.lastUpdate,
        address: state.address,
        distance: state.distance,
        icon: state.icon,
        message: state.message,
        additionalInformations: state.additionalInformations,
        animals: state.animals,
        selectedService: state.selectedService,
        formSlotsByService: state.formSlotsByService,
        petsittersIds: state.petsittersIds,
        initialized: state.initialized,
        petsToComplete: state.petsToComplete,
        hasCompleteSearch: state.hasCompleteSearch,
      }),
    },
  ),
)

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORE_NAME || !event.newValue) return

    try {
      const parsed = JSON.parse(event.newValue)
      const persistedState = parsed?.state

      if (!persistedState) return

      useNewRequestCartStore.getState().hydrateFromPersistedState(persistedState)
    } catch (error) {
      console.error('Error syncing newRequestCartStore across tabs:', error)
    }
  })
}

export const expandDateTimeInstance = (instance: DateTimeInstance, service: ServiceSlug): DateTimeInstance[] => {
  const days = buildRecurrentDateList(instance)
  const results: DateTimeInstance[] = []

  for (const date of days) {
    for (const slot of instance.timePeriods || []) {
      if (!slot.timeStart) continue

      results.push({
        date,
        timePeriods: [
          {
            timeStart: slot.timeStart || '00:00',
            serviceDuration: slot.serviceDuration,
          },
        ],
      })
    }
  }

  return results
}
